import { Storage } from "@/services/Storage";
import axios from "axios";
import { responses } from "@/utils/responses";
import router from "@/router";

export default {
    name: 'user',
    state: {
        admin: '',
        currentUser: {
            Type: "",
            Username: "",
            id: "",
            Surname: "",
            Name: "",
            Password: ""
        },
        securities: [],
        users: [],
        allUsers: [],
        supervisor: null,
    },
    getters: {
        getCurrentUser: (state) => state.currentUser,
        getAllUsers: (state) => state.allUsers,
        getAdmin: (state) => {
            if (state.admin) {
                return JSON.parse(window.atob(state.admin))
            }
        },
        getUsers: (state) => state.users,
        getUserByPass: (state) => async (password) => {
            // DEPRECATED after password-encryption rollout (2026-06-18).
            // The cached users list no longer carries `Password`, so this
            // compare always returns undefined. Use `dispatch('posLogin')`
            // (backend PIN verification) instead.
            console.warn('[user_module] getUserByPass is deprecated after the password-encryption rollout. Use dispatch("posLogin").');
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.Password && u.Password.toLowerCase() === password.toLowerCase());
        },

        countNonLytSoftUsers: async (state) => {
            state.users = await Storage.getAllUsers();
            return state.users.filter(user => user.Username !== 'Admin').length;
        },

        getUserByID: (state) => async (id) => {
            state.users = await Storage.getAllUsers();
            return state.users.find(u => u.id === id);
        },
        getSecurities: (state) => state.securities,
        getSupervisor: (state) => state.supervisor,
    },
    mutations: {
        setAdmin: async (state, data) => {
            state.admin = window.btoa(JSON.stringify(data));
        },
        setCurrentUser: async (state, data) => {
            state.currentUser = data;
        },
        setAllSecurities: async (state, data) => {
            if (!data) data = await Storage.getAllSecurities();
            state.securities = data;
        },
        setAllUsers: async (state, data) => {
            if (!data) {
                data = await Storage.getAllUsers();
            }
            state.users = data
        },
        setSupervisor(state, supervisor) {
            state.supervisor = supervisor;
        },
    },
    actions: {
        async recordlogin(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username,
                platform: 'RetailPOS',
                pc: device,
            }
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogin`, payload)
        },

        async recordLogout(context, username) {
            let device = await Storage.getDeviceSettings();
            device = device.PC;
            let store = await Storage.getDefaultStore();
            if (!store) return;
            const payload = {
                username: username,
                platform: 'RetailPOS',
                pc: device,
            }
            return axios.post(serverApi + `supervisorOverrides/${store.StoreDB}/recordLogout`, payload)
        },

        getAllUsers: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            if (!store) {
                let route = 'login';
                let allStores = context.getters.getRegisteredStores;
                if (allStores.length > 0) {
                    route = 'setup';
                }
                responses.showInfo(`Welcome , Please Login`)
                return router.push({ name: route })
            }
            return axios.get(franchiseTomcatApi + `employee/${store.StoreDB}/getAll`)
                .then(({ data }) => {
                    context.commit('setAllUsers', data.filter(user => !user.archived));
                    return data;
                }).catch((err) => errorHandler.tomcatError(err))
        },

        posLogin: async (context, data) => {
            // Backend PIN verification.
            //
            // After the password-encryption rollout (Argon2id hashes stored
            // in emp.Password) the client can no longer compare the typed
            // PIN against the cached users list - hashes are one-way.
            // Verification has to move server-side, so the POS now calls
            // POST {franchiseTomcatApi}/posAuth/{DB}/login which uses
            // PasswordEncryptionService.verify() under the hood.
            //
            // See docs/PASSWORD-ENCRYPTION-ROLLOUT.md for the full contract.
            try {
                let store = await Storage.getDefaultStore();
                if (!store || !store.StoreDB) {
                    return { isValid: false, username: null };
                }
                const { data: resp } = await axios.post(
                    franchiseTomcatApi + `posAuth/${store.StoreDB}/login`,
                    { password: data }
                );
                if (!resp || !resp.success) {
                    return { isValid: false, username: null };
                }
                // Deeplink session: the login response carries an opaque
                // sessionId + sessionExpiresAt. Persist them now so
                // openOnlineBackOffice can build a deeplink URL without
                // ever touching the user's password again.
                if (resp.sessionId) {
                    try {
                        await Storage.setCurrentSession(resp, store.StoreDB);
                    } catch (e) {
                        // Best-effort: a storage failure must not block login.
                        // eslint-disable-next-line no-console
                        console.warn('[posLogin] failed to persist deeplink session', e);
                    }
                }
                // Find the matching cached user so we keep the same shape
                // for downstream callers (setCurrentUser, getAllStock, etc.).
                const currentUser = context.getters.getUsers.find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (currentUser) {
                    context.commit('setCurrentUser', currentUser);
                    context.dispatch('getAllStock');
                    return { isValid: true, username: currentUser.Username };
                }
                // Cache miss - refresh roster from server and retry once.
                await context.dispatch('getAllUsers');
                const refreshed = context.getters.getUsers.find(
                    u => u.id === resp.empId
                    || (u.Username && resp.username
                        && u.Username.toLowerCase() === resp.username.toLowerCase())
                );
                if (refreshed) {
                    context.commit('setCurrentUser', refreshed);
                    context.dispatch('getAllStock');
                    return { isValid: true, username: refreshed.Username };
                }
                // Still no match - return the username from the server so the
                // UI can advance. setCurrentUser will be reconciled later.
                return { isValid: true, username: resp.username };
            } catch (err) {
                const status = err && err.response && err.response.status;
                return { isValid: false, username: null, status };
            }
        },

        async editEmployee(context, payload) {
            let store = await Storage.getDefaultStore();
            return axios.put(franchiseTomcatApi + `employee/${store.StoreDB}/${payload.id}`, payload)
                .then(() => context.dispatch(`getAllUsers`))
                .catch((err) => errorHandler.tomcatError(err))
        },

        createEmployee: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `employee/${store.StoreDB}`, payload)
                .then(() => context.dispatch('getAllUsers'))
                .catch((err) => errorHandler.tomcatError(err))
        },

        forceCashUp: async () => {
            let store = await Storage.getDefaultStore();
            let currentUser = await Storage.getCurrentUser()
            let settings = await Storage.getDeviceSettings()
            return axios.get(franchiseTomcatApi + `cashup/${store.StoreDB}/forceCashup?user=` + currentUser.Username + '&pc=' + settings.PC)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        async generateStoreOTP() {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `stores/${store.StoreDB}/generateOTP`)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },
        validateSecurityPermissionNoPrompt: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                console.warn(`No security group found`)
                return false;
            }
            return group[security];
        },

        validateSecurityPermission: async (context, security) => {
            let securities = await Storage.getAllSecurities();
            let user = await Storage.getCurrentUser();
            let userType = user.Type;
            let group = securities.find(sec => sec.GroupName.toLowerCase() === userType.toLowerCase());
            if (!group) {
                await responses.showWarning(`No security group found`)
                return false;
            }
            if (!group[security]) {
                $utils.hideLoading();
                return context.dispatch('checkUserSecurity', security).then(async (result) => {
                    if (result.isConfirmed) {
                        let value = result.value;
                        if (value.allowed) {
                            let store = await Storage.getDefaultStore();
                            await Storage.setSupervisor(value.supervisor);
                            axios.post(serverApi + `supervisorOverrides/${store.StoreDB}`, {
                                Supervisor: value.supervisor,
                                Cashier: value.cashier,
                                Reason: security,
                            })
                        }
                        return value.allowed;
                    }
                    return false;
                });

            }
            if (security.toString().toLowerCase().includes(`void`)) {
                return { supervisor: user.Username }
            }
            return true;
        },

        async getAllSecurities(context) {
            let store = await Storage.getDefaultStore();
            if (!store) return;
            return axios.get(franchiseTomcatApi + `empsecurity/${store.StoreDB}/getAll`)
                .then(({ data }) => context.commit(`setAllSecurities`, data))
                .catch((err) => errorHandler.tomcatError(err))
        },

    }
}
